import * as React from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import OutlinedInput from "@mui/material/OutlinedInput";
import { forgotPassword } from "../../../services/authService";
import { Alert, CircularProgress, Snackbar } from "@mui/material";
import { useState } from "react";

interface ForgotPasswordProps {
  open: boolean;
  handleClose: () => void;
}

export default function ForgotPassword({
  open,
  handleClose,
}: ForgotPasswordProps) {
  const [formError, setFormError] = useState("");
  const [errorSnackbarOpen, setErrorSnackbarOpen] = useState(false);
  const [resetLinkSent, setResetLinkSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const email = (
      event.currentTarget.elements.namedItem("email") as HTMLInputElement
    ).value;

    setFormError(""); // Reset previous error
    setLoading(true);

    try {
      await forgotPassword(email);
      setResetLinkSent(true);
    } catch (error: any) {
      console.error("Error during password reset request:", error);
      const message =
        error?.response?.data?.error || "Failed to send reset link.";

      setFormError(message);
      setErrorSnackbarOpen(true);
    } finally {
      // Only stop loading and reset states when no error occurs
      if (!formError) {
        setLoading(false); // Stop loading
        handleClose();
      }
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        slotProps={{
          paper: {
            component: "form",
            onSubmit: handleSubmit,
            sx: { backgroundImage: "none" },
          },
        }}
      >
        <DialogTitle>Reset password</DialogTitle>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            width: "100%",
          }}
        >
          <DialogContentText>
            Enter your account&apos;s email address, and we&apos;ll send you a
            link to reset your password.
          </DialogContentText>
          <OutlinedInput
            autoFocus
            required
            margin="dense"
            id="email"
            name="email"
            label="Email address"
            placeholder="Email address"
            type="email"
            fullWidth
          />
        </DialogContent>
        <DialogActions sx={{ pb: 3, px: 3 }}>
          <Button onClick={handleClose}>Cancel</Button>
          <Button variant="contained" type="submit" disabled={loading}>
            {loading ? <CircularProgress size={24} color="info" /> : "Continue"}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Snackbar for invitation sent */}
      <Snackbar
        open={resetLinkSent}
        autoHideDuration={3000}
        onClose={() => setResetLinkSent(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setResetLinkSent(false)}
          severity="success"
          sx={{ width: "100%" }}
        >
          We've sent a password reset link to your email.
        </Alert>
      </Snackbar>
      {/* Snackbar for error */}
      <Snackbar
        open={errorSnackbarOpen}
        autoHideDuration={10000}
        onClose={() => setErrorSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setErrorSnackbarOpen(false)}
          severity="error"
          sx={{ width: "100%" }}
        >
          {formError}
        </Alert>
      </Snackbar>
    </>
  );
}
